"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import styles from "./Tabs.module.css";

export interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTab: string;
  ariaLabel?: string;
  className?: string;
  reducedBottomSpacing?: boolean;
}

export default function Tabs({
  tabs,
  defaultTab,
  ariaLabel = "Page navigation",
  className = "",
  reducedBottomSpacing = false,
}: TabsProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const currentTab = searchParams.get("tab") || defaultTab;

  const handleTabChange = (tabId: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("tab", tabId);

    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  return (
    <div
      className={`${styles.contentContainer} ${reducedBottomSpacing ? styles.contentContainerReduced : ""}`}
    >
      <nav className={`${styles.tabNav} ${className}`} aria-label={ariaLabel}>
        <div className={styles.tabGroup} role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              aria-controls={`panel-${tab.id}`}
              aria-selected={currentTab === tab.id}
              className={currentTab === tab.id ? styles.tabActive : styles.tab}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`panel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
          hidden={currentTab !== tab.id}
        >
          {currentTab === tab.id && tab.content}
        </div>
      ))}
    </div>
  );
}
